import {
  Button,
  FilesPdfIcon,
  FlagIcon,
  Flex,
  Image,
  Loader,
  Text,
} from "@fluentui/react-northstar";
import React, { useEffect, useRef } from "react";
import ProgressChart from "../../../../../components/studentProgress/ProgressChart";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import { Card } from "antd";
import "./StudentProgressCard.scss";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import LabelBox from "./components/LabelBox";
import DataBox from "./components/DataBox";
import CustomFieldsBox from "./components/CustomFieldsBox";

const chartTypes = {
  attendance: "attendance",
  assignment: "assignment",
  event: "event",
  feedback: "feedback",
};

const StudentProgressCard = (props) => {
  const { selectedStudent: student } = props;
  const { t } = useTranslation();

  const [loadedInfo, setLoadedInfo] = React.useState(null);
  const [studentStatistics, setStudentStatistics] = React.useState([]);
  const [customFieldInfo, setCustomFieldInfo] = React.useState(null);
  const [selectedChart, setSelectedChart] = React.useState(
    chartTypes.attendance
  );
  const [loader, setLoader] = React.useState(false);

  const studentCardRef = useRef();

  const getInfo = (email) => {
    setLoader(true);
    setLoadedInfo(null);
    setCustomFieldInfo(null);
    setSelectedChart(chartTypes.attendance);
    axios
      .put(
        `${process.env.REACT_APP_EP_URL}/api/admin/get-student-info/${email}${props.user.slug}`,
        null,
        {
          headers: {
            Authorization: "Bearer " + props.user.accessToken,
          },
        }
      )
      .then((res) => {
        const response = res.data.result;
        const { assignment, feedback, event, info, chart } = res.data;
        setLoadedInfo({
          ...response,
          Ausente: response?.Ausente ?? 0,
          Justificado: response?.Justificado ?? 0,
          Presente: response?.Presente ?? 0,
          Retardo: response?.Retardo ?? 0,
          StudentEmailId: response?.StudentEmailId ?? "",
          StudentName: response?.StudentName ?? "",
          Total: response?.Total ?? 0,

          assignment: {
            total: assignment?.total ?? 0,
            reSubmit: assignment?.Resubmit ?? 0,
            completed: assignment?.Completed ?? 0,
            pending: assignment?.Pending ?? 0,
            gradesPending: assignment?.["Grades Pending"] ?? 0,
          },
          event: {
            registered: event?.registered ?? 0,
            bookmarked: event?.bookmarked ?? 0,
            organized: event?.organized ?? 0,
            total: event?.total ?? 0,
          },
          feedback: {
            total: feedback?.total ?? 0,
            poll: feedback?.poll ?? 0,
            text: feedback?.text ?? 0,
            audio: feedback?.audio ?? 0,
            video: feedback?.video ?? 0,
            mixed: feedback?.mixed ?? 0,
            any: feedback?.any ?? 0,
          },
          info: {
            ...info,
            subjects:
              info.subjects && Array.isArray(info.subjects)
                ? info.subjects
                : [],
            classNumber: info.classNumber ?? "",
            attendanceAverage: info.attendanceAverage ?? 0,
            assignmentAverage: info.assignmentAverage ?? 0,
          },
          chart,
        });
        const studentStatisticsData = [
          {
            header: t("setting")?.student?.attendance[0],
            chartType: chartTypes.attendance,
            info: {
              [t("setting").student.attendance[2]]: response?.Total ?? 0,
              [t("setting").student.attendance[3]]: response?.Presente ?? 0,
              [t("setting").student.attendance[5]]: response?.Retardo ?? 0,
              [t("setting").student.attendance[4]]: response?.Ausente ?? 0,
              [t("setting").student.attendance[6]]: Number(
                response?.Justificado ?? 0
              ),
            },
          },
          {
            header: t("setting").student.assignment[0],
            chartType: chartTypes.assignment,
            info: {
              [t("setting").student.assignment[1]]: assignment?.total ?? 0,
              [t("setting").student.assignment[3]]: assignment?.Resubmit ?? 0,
              [t("setting").student.assignment[2]]: assignment?.Completed ?? 0,
              [t("setting").student.assignment[4]]: assignment?.Pending ?? 0,
              [t("setting").student.assignment[5]]:
                assignment?.["Grades Pending"] ?? 0,
            },
          },
          {
            header: t("setting").student.event[0],
            chartType: chartTypes.event,
            info: {
              [t("setting").student.event[2]]: event?.registered ?? 0,
              [t("setting").student.event[3]]: event?.bookmarked ?? 0,
              [t("setting").student.event[4]]: event?.organized ?? 0,
              [t("setting").student.event[1]]: event?.total ?? 0,
            },
          },
          {
            header: t("setting").student.feedback[0],
            chartType: chartTypes.feedback,
            info: {
              [t("setting").student.feedback[1]]: feedback?.total ?? 0,
              [t("setting").student.feedback[2]]: feedback?.poll ?? 0,
              [t("setting").student.feedback[3]]: feedback?.text ?? 0,
              [t("setting").student.feedback[4]]: feedback?.audio ?? 0,
              [t("setting").student.feedback[5]]: feedback?.video ?? 0,
            },
          },
        ];
        setStudentStatistics(studentStatisticsData);
        setCustomFieldInfo(res.data.custom_field);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        toast.error(err?.response?.data?.message);
        console.log(err);

        if (err.response?.status === 401) {
          setAccDlg(true);
        }
      });
  };

  const chartSelectHandler = (type) => {
    setSelectedChart(type);
  };

  useEffect(() => {
    if (student) {
      getInfo(student.key);
    }
  }, [student]);

  const uniqueClassNumbers = [...new Set(loadedInfo?.info?.classNumber || [])];

  const customFieldData = customFieldInfo?.reduce((acc, cf) => {
    acc[cf.Name] = loadedInfo.StudentData["CF_" + cf.CustomFieldID];
    return acc;
  }, {});

  const downloadPDF = async () => {
    if (!studentCardRef.current) return;

    const input = studentCardRef.current;

    // Ensure all images are loaded before capturing
    const imgPromises = Array.from(input.querySelectorAll("img")).map((img) => {
      return new Promise((resolve) => {
        if (img.complete) resolve();
        else {
          img.onload = resolve;
          img.onerror = resolve; // Prevent blocking if an image fails
        }
      });
    });

    await Promise.all(imgPromises);

    try {
      const canvas = await html2canvas(input, { useCORS: true }); // Higher quality
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190; // Adjust to fit A4
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save("student-card.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  return (
    <Card ref={studentCardRef}>
      <Flex gap="gap.medium" className="student-data-wrapper" wrap>
        <Flex column className="student-image-info-wrapper" gap="gap.medium">
          <Image src={student?.image} className="m-img" />
          {loadedInfo ? (
            <Flex column className="student-info-wrapper" gap="gap.smaller">
              <Flex gap="gap.small" column>
                <LabelBox label={`${t("setting").student.info[0]}:`} />
                <Text
                  content={loadedInfo?.StudentName || ""}
                  truncated
                  title={loadedInfo?.StudentName}
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${t("setting").student.info[1]}:`} />
                <Text
                  content={loadedInfo?.StudentEmailId || ""}
                  truncated
                  title={loadedInfo?.StudentEmailId}
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${t("setting").student.info[2]}:`} />
                <Text
                  content={uniqueClassNumbers?.join(", ") ?? ""}
                  truncated
                  title={uniqueClassNumbers?.join(", ") ?? ""}
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${t("setting").student.info[3]}:`} />
                <Text
                  content={loadedInfo?.info?.attendanceAverage ?? ""}
                  truncated
                  title={loadedInfo?.info?.attendanceAverage ?? ""}
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${t("setting").student.info[4]}:`} />
                <Text
                  content={loadedInfo?.info?.assignmentAverage ?? ""}
                  truncated
                  title={loadedInfo?.info?.assignmentAverage ?? ""}
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${t("setting").student.info[5]}:`} />

                {Array.isArray(loadedInfo?.info?.subjects) ? (
                  <Flex column className="subjects-wrapper">
                    {loadedInfo.info.subjects.map((item, i) => (
                      <Text
                        content={item ?? ""}
                        truncated
                        title={item ?? ""}
                        style={{ padding: "0 5px" }}
                        key={`subject-${i + 1}`}
                      />
                    ))}
                  </Flex>
                ) : (
                  <Text
                    content={`${t("setting").student.info[6]}`}
                    truncated
                    title={`${t("setting").student.info[6]}`}
                    style={{ padding: "0 5px" }}
                  />
                )}
              </Flex>
            </Flex>
          ) : (
            ""
          )}


        </Flex>
        <Flex column className="info-chart-wrapper">
          {loadedInfo ? (
            <Flex column gap="gap.large">
              <Flex
                gap="gap.large"
                fill
                className="student-statictics-wrapper"
                space="between"
                style={{ overflowX: "auto" }}
              >
                {studentStatistics.map((data, i) => {
                  return (
                    <DataBox
                      key={`student-statistics-data-${i}`}
                      header={data.header}
                      info={data.info}
                      changeChartType={() => chartSelectHandler(data.chartType)}
                    />
                  );
                })}
              </Flex>
              <Flex gap="gap.large">
                <ProgressChart
                  selectedChart={selectedChart}
                  data={loadedInfo.chart}
                  chartTypes={chartTypes}
                />
                <Flex
                  style={{ maxWidth: "350px", minWidth: "300px" }}
                  vAlign="center"
                  hAlign="end"
                  column
                  gap="gap.large"
                >
                  {customFieldData && (
                    <CustomFieldsBox
                      header={`${t("setting").student.attendance[1]}`}
                      icon={<FlagIcon outline />}
                      info={customFieldData}
                    />
                  )}
                  <Flex
                    style={{ width: "fit-content" }}
                    column
                    gap="gap.medium"
                  >
                    <div
                      style={{
                        backgroundColor: "#f2f2f2",
                        padding: "2px 30px",
                      }}
                    >
                      <Text
                        content={t("teacherReportCard").common.downloadCard}
                        weight="semibold"
                        align="center"
                      />
                    </div>
                    <Button
                      icon={<FilesPdfIcon size="largest" />}
                      iconOnly
                      text
                      onClick={downloadPDF}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          ) : (
            loader && (
              <Flex fill hAlign="center" vAlign="center" column>
                <Loader size="small" />
              </Flex>
            )
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

export default StudentProgressCard;
